import { HttpContextToken, HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthService } from 'ok-angular-shared/auth';
import { SnackbarService } from 'ok-angular-shared/components';
import { catchError, from, switchMap, throwError } from 'rxjs';

// Marks a request as already having gone through a refresh-and-retry attempt,
// so a 401 on the retried request doesn't trigger another refresh (avoids loops).
const IS_RETRY_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

export const h401Interceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const oauthService = inject(OAuthService);
  const snackbarService = inject(SnackbarService);
  const message = 'Din session er udløbet. Genindlæs venligst siden';

  const showSessionExpiredMessage = () => {
    const snackBarRef = snackbarService.error(message);

    snackBarRef?.afterDismissed().subscribe(info => {
      if (info.dismissedByAction === true) {
        authService.logout();
      }
    });
  };

  return next(req).pipe(
    catchError((err: unknown) => {
      const isUnauthorized = err instanceof HttpErrorResponse && err.status === 401;
      const alreadyRetried = req.context.get(IS_RETRY_AFTER_REFRESH);

      if (!isUnauthorized || alreadyRetried) {
        if (isUnauthorized) {
          showSessionExpiredMessage();
        }
        return throwError(() => err);
      }

      return from(oauthService.refreshToken()).pipe(
        switchMap(() => {
          const accessToken = oauthService.getAccessToken();
          const retriedReq = req.clone({
            setHeaders: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
            context: req.context.set(IS_RETRY_AFTER_REFRESH, true),
          });
          return next(retriedReq);
        }),
        catchError(retryErr => {
          showSessionExpiredMessage();
          return throwError(() => retryErr);
        })
      );
    })
  );
};
